import { CloseWorkOrderModalWithAttachments } from "@/shared/work-orders-modals/components/modal-close-work-order";
import { DeleteMultipleWorkOrdersModal } from "@/shared/work-orders-modals/components/modal-delete-multiple-work-orders";
import { DeleteWorkOrderModal } from "@/shared/work-orders-modals/components/modal-delete-work-order";
import { EditWorkOrderModal } from "@/shared/work-orders-modals/components/modal-edit-work-order";
import { OpenWorkOrderModal } from "@/shared/work-orders-modals/components/modal-open-work-order";
import {
	type ResourceType,
	TableWorkOrdesResoures,
} from "@/shared/work-orders-modals/components/work-order-resources/table-work-ordes-resoures";
import { INITIAL_VALUES_MODAL } from "@/shared/work-orders-modals/models/ModalInitialValue";
import {
	AssetTreePicker,
	dxtToLocalServerTime,
	ErrorModal,
	useUserContext,
} from "@dexteel/mesf-core";
import { Button, Grid, Paper, Snackbar, Typography } from "@material-ui/core";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import { Alert } from "@material-ui/lab";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	ICellRendererParams,
	IContextMenuParams,
	MenuItemDef,
	RowNode,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomLoadingOverlay from "../../components/loaders/LazyLoading/LazyLoading";
import { AssetNameColumnColumn } from "./components/assetName-column";
import { CloseGroupWorkOrdersDialog } from "./components/close-group-wo-dialog/close-group-wo-dialog";
import { WorkOrdersCommentsColumn } from "./components/comments-buttons/comments-buttons.columns";
import { WorkOrdersResourcesColumn } from "./components/resources-buttons/resources-buttons.columns";
import { useWorkOrdersContext } from "./context/workOrdersContext";
import { Filters } from "./filters";
import { useInitializeWorkOrders } from "./hooks/useInitializeWorkOrders";
import { useLoadfilters } from "./hooks/useLoadFilters";
import { useSearchData } from "./hooks/useSearchData";
import type { WorkOrder } from "./models/WorkOrder";

export default function WorkOrders() {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const {
		state: {
			dataToGetWorkOrders: { AssetId, assetSearchName },
			rowData,
			noResults,
			assetCodes,
			listWorkOrderSelected,
			showDialogToCloseGroup,
			openWorkOrderResources,
		},
		actions: {
			setListWorkOrderSelected,
			setNoResults,
			setFirstTime,
			setShowDialogToCloseGroup,
			setOpenWorkOrderResources,
		},
	} = useWorkOrdersContext();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isCraftLoading, setIsCraftLoading] = useState<boolean>(false);
	const [craftError, setCraftError] = useState<string>("");
	const { loadFilterCrafts, loadFilterCloser } = useLoadfilters({
		setIsCraftLoading,
		setCraftError,
	});
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");

	const searchData = useSearchData({ setIsLoading, setError });

	const [selectedRow, setSelectedRow] = useState(INITIAL_VALUES_MODAL);
	const [selectedRows, setSelectedRows] = useState<WorkOrder[]>([]);

	const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
	const [selectedResource, setSelectedResource] = useState<ResourceType | null>(
		null,
	);

	const navigate = useNavigate();
	const gridRef = useRef<AgGridReact>(null);

	const [openWorkOrderEditModal, setOpenWorkOrderEditModal] =
		useState<boolean>(false);
	const [openCloseWorkOrderModal, setOpenCloseWorkOrderModal] =
		useState<boolean>(false);
	const [openWorkOrderModal, setOpenWorkOrderModal] = useState<boolean>(false);
	const [openDeleteWorkOrderModal, setOpenDeleteWorkOrderModal] =
		useState<boolean>(false);
	const [
		openDeleteMultipleWorkOrdersModal,
		setOpenDeleteMultipleWorkOrdersModal,
	] = useState<boolean>(false);

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setNoResults(false);
	};

	const onMenuOptionSelected = (
		option:
			| "EDIT"
			| "EDIT-COMMENTS"
			| "CLOSE-ORDER"
			| "OPEN-ORDER"
			| "DELETE-ORDER"
			| "DELETE-MULTIPLE-ORDER",
		row: any,
	) => {
		if (option !== "DELETE-MULTIPLE-ORDER") {
			setSelectedRow(row);
		} else if (option === "DELETE-MULTIPLE-ORDER") {
			setSelectedRows(
				(row as RowNode<WorkOrder>[])
					.map((node) => node.data)
					.filter((data): data is WorkOrder => data !== undefined),
			);
		}

		switch (option) {
			case "EDIT":
			case "EDIT-COMMENTS":
				setOpenWorkOrderEditModal(true);
				break;
			case "CLOSE-ORDER":
				setOpenCloseWorkOrderModal(true);
				break;
			case "OPEN-ORDER":
				setOpenWorkOrderModal(true);
				break;
			case "DELETE-ORDER":
				setOpenDeleteWorkOrderModal(true);
				break;
			case "DELETE-MULTIPLE-ORDER":
				setOpenDeleteMultipleWorkOrdersModal(true);
				break;
		}
	};

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		const contextMenuParams: IContextMenuParams = {
			rowNode: params.node,
			column: params.column,
			value: params.node?.data,
			x: e.nativeEvent.clientX,
			y: e.nativeEvent.clientY,
			source: "ui",
		};
		params.api.showContextMenu(contextMenuParams);
	};

	const renderCategoryCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.CraftColor || "#FFFFFF",
					}}
				>
					<div style={{ color: "#FFF", fontWeight: 600 }}>
						<div>{props.data?.CraftName?.charAt(0) || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const renderTypeCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.TypeCode == "PM" ? "#00008B" : "#D3D3D3",
					}}
				>
					<div
						style={{
							color: props.data?.TypeCode == "PM" ? "#FFFFFF" : "#000000",
							fontWeight: 600,
						}}
					>
						<div>{props.data?.TypeCode || ""}</div>
					</div>
				</div>
			</div>
		);
	};

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const listOfWorkOrders = event.api.getSelectedNodes();
		setListWorkOrderSelected(listOfWorkOrders);
	}, []);

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const openResourceModal = useCallback(
		(resource: ResourceType, rowData: any) => {
			setSelectedRow(rowData);
			setSelectedResource(resource);
			setOpenWorkOrderResources(true);
		},
		[],
	);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const status = params.node?.data.WorkOrderStatusCode;
			const typeCode = params.node?.data.TypeCode;
			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "Edit",
					action: () => {
						onMenuOptionSelected("EDIT-COMMENTS", params.node?.data);
					},
					icon: '<i class="fas fa-edit"></i>',
				},
			];

			if (status === "O") {
				result.push({
					name: "Close Work Order",
					action: () => {
						if (listWorkOrderSelected?.length > 1) {
							setShowDialogToCloseGroup(true);
						} else onMenuOptionSelected("CLOSE-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-times-circle"></i>',
				});
			}

			if (status === "C") {
				result.push({
					name: "Open Work Order",
					action: () => {
						onMenuOptionSelected("OPEN-ORDER", params.node?.data);
					},
					icon: '<i class="far fa-plus-square"></i>',
				});
			}

			if (typeCode === "PM" && params.node?.data.TemplateId) {
				result.push({
					name: "Go To Template",
					action: () => {
						const templateId = params.node?.data.TemplateId;
						const assetId = params.node?.data.AssetId;
						navigate(`/templates/${assetId}?templateId=${templateId}`);
					},
					icon: '<i class="fas fa-file-alt"></i>',
				});
			}

			result.push(
				"separator",
				{
					name: "Work Order Drawings",
					action: () => openResourceModal("DRAWINGS", params.node?.data),
					icon: '<i class="far fa-file-image"></i>',
				},
				{
					name: "Work Order Parts",
					action: () => openResourceModal("PARTS", params.node?.data),
					icon: '<i class="fas fa-tools"></i>',
				},
				{
					name: "Work Order Attachments",
					action: () => openResourceModal("ATTACHMENTS", params.node?.data),
					icon: '<i class="fas fa-paperclip"></i>',
				},
				{
					name: "Work Order LOTO",
					action: () => openResourceModal("LOTO", params.node?.data),
					icon: '<i class="fas fa-tag"></i>',
				},
				"separator",
			);

			if (params.api.getSelectedNodes().length > 1) {
				result.push({
					name: "Delete Work Orders",
					action: () => {
						onMenuOptionSelected(
							"DELETE-MULTIPLE-ORDER",
							params.api.getSelectedNodes() as RowNode<WorkOrder>[],
						);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			} else {
				result.push({
					name: "Delete Work Order",
					action: () => {
						onMenuOptionSelected("DELETE-ORDER", params.node?.data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			return params.node?.data.WorkOrderId ? result : [];
		},
		[listWorkOrderSelected, openResourceModal, navigate],
	);

	const onDobleClicked = () => {
		const selectedRow1 = gridRef.current!.api.getSelectedNodes();
		onMenuOptionSelected("EDIT", selectedRow1[0].data);
	};

	const changeAsset = (value: number | null) => {
		navigate(`/work-orders/${value}`, { replace: true });
	};

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			searchData();
		}
	};

	useInitializeWorkOrders();

	useEffect(loadFilterCloser, []);

	useEffect(() => {
		loadFilterCrafts();
	}, []);

	useEffect(() => {
		setColumnDefs([
			{
				colId: "checkbox",
				flex: 1,
				maxWidth: 33,
				filter: false,
				checkboxSelection: true,
				headerCheckboxSelection: true,
			},
			{
				field: "WorkOrderName",
				headerName: "Name",
				minWidth: 100,
				flex: 1,
				headerClass: "ag-center-header",
			},
			{
				field: "Asset",
				headerName: "Asset",
				cellRenderer: AssetNameColumnColumn,
				minWidth: 150,
				flex: 2,
				valueGetter: (params) => params.data.FullPath,
			},
			{ field: "Title", headerName: "Title", minWidth: 170, flex: 3 },
			{
				field: "OpenTimestamp",
				headerName: "Open",
				minWidth: 110,
				maxWidth: 110,

				flex: 1,
				valueGetter: (params) =>
					dxtToLocalServerTime(params.data.OpenTimestamp, "MM/dd/yy HH:mm"),
			},
			{
				field: "CloseTimestamp",
				headerName: "Closed",
				minWidth: 110,
				maxWidth: 110,
				flex: 1,
				valueGetter: (params) =>
					dxtToLocalServerTime(params.data.CloseTimestamp, "MM/dd/yy HH:mm"),
			},
			{
				field: "TypeCode",
				headerName: "Type",
				minWidth: 40,
				maxWidth: 40,
				flex: 1,
				cellRenderer: renderTypeCell,
			},
			{
				field: "CraftName",
				headerName: "Craft",
				minWidth: 40,
				maxWidth: 40,
				flex: 1,
				cellRenderer: renderCategoryCell,
			},
			{
				field: "DownHours",
				headerName: "Down Hours",
				minWidth: 80,
				maxWidth: 80,
				flex: 1,
			},
			{
				headerName: "",
				flex: 1,
				minWidth: 220,
				cellClass: "ag-columns",
				wrapText: false,
				autoHeight: false,
				cellRenderer: (params: EditableCallbackParams) => {
					return (
						<Grid
							container
							style={{
								height: "100%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid
								item
								xs={4}
								md={4}
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<WorkOrdersCommentsColumn
									searchData={searchData}
									params={params}
								/>
							</Grid>
							<Grid
								item
								xs={4}
								md={4}
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<WorkOrdersResourcesColumn
									params={params}
									setSelectedRow={setSelectedRow}
									openResourceModal={openResourceModal}
								/>
							</Grid>
							<Grid
								item
								xs={4}
								md={4}
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
							>
								<Grid
									container
									spacing={1}
									style={{ justifyContent: "flex-end", minWidth: 30 }}
								>
									<Grid item xs={12} md={12}>
										<Button
											fullWidth
											style={{}}
											onClick={(e) => onActionsClick(e, params)}
										>
											<FormatListBulletedSharpIcon
												style={{ height: "auto" }}
												color="action"
											/>
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					);
				},
			},
		]);
	}, [AssetId, openResourceModal]);

	return (
		<>
			{openWorkOrderResources && selectedResource && (
				<TableWorkOrdesResoures
					show={openWorkOrderResources}
					onHide={(shouldUpdate: boolean) => {
						setOpenWorkOrderResources(false);
						setSelectedResource(null);
						if (shouldUpdate) {
							searchData();
						}
					}}
					AssetId={selectedRow?.AssetId}
					WorkOrderId={selectedRow?.WorkOrderId}
					initialResource={selectedResource}
					WorkOrderName={selectedRow?.WorkOrderName as string}
					Title={selectedRow?.Title as string}
					AssetName={selectedRow?.AssetName as string}
					TypeCode={selectedRow?.TypeCode as string}
					CraftId={selectedRow?.CraftId as number}
					OpenTimestamp={selectedRow?.OpenTimestamp as Date}
					CloseTimestamp={selectedRow?.CloseTimestamp as Date}
				/>
			)}
			<Grid
				container
				style={{ padding: "10px 20px" }}
				onContextMenu={(e) => e.preventDefault()}
			>
				<Grid container alignItems="center" justifyContent="center" spacing={1}>
					<Grid item md={12} xs={12}>
						<Typography
							variant="h5"
							style={{
								margin: "0",
								fontWeight: 600,
								userSelect: "none",
								marginLeft: 5,
							}}
						>
							WORK ORDERS
						</Typography>
					</Grid>

					<Grid item xs={12}>
						<Paper elevation={1} style={{ padding: 10, marginBottom: 20 }}>
							<Grid container spacing={1}>
								<Grid item lg={2} md={12} xs={12}>
									<AssetTreePicker
										label="Asset"
										value={Number.parseInt(AssetId as string) || 0}
										onChange={changeAsset}
									/>
								</Grid>

								<Grid item lg={10} md={12} xs={12}>
									<Filters searchData={searchData} onKeyDown={onKeyDown} />
								</Grid>
							</Grid>
						</Paper>
					</Grid>
				</Grid>

				<Paper
					elevation={1}
					style={{
						width: "100%",
						padding: 10,
						borderRadius: "0.75rem",
					}}
				>
					<Grid
						item
						md={12}
						xs={12}
						style={{
							height: "65vh",
						}}
					>
						<AgGridReact
							ref={gridRef}
							rowData={rowData}
							columnDefs={columnDefs}
							defaultColDef={globalDefaultColDef}
							rowHeight={33}
							headerHeight={32}
							animateRows={true}
							loadingOverlayComponent={loadingOverlayComponent}
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={onDobleClicked}
							onSelectionChanged={onSelectionChanged}
							rowSelection={"multiple"}
							loading={isLoading}
							onGridReady={(params) => setGridApi(params.api)}
						/>
					</Grid>
				</Paper>
			</Grid>
			<Snackbar open={noResults} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="error" onClose={handleClose}>
					No results found
				</Alert>
			</Snackbar>
			<CloseGroupWorkOrdersDialog
				show={showDialogToCloseGroup}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchData();
						setListWorkOrderSelected([]);
					}
					setShowDialogToCloseGroup(false);
				}}
				suffixTitle="Work Orders"
			/>
			<EditWorkOrderModal
				show={openWorkOrderEditModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchData();
					}
					setOpenWorkOrderEditModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<CloseWorkOrderModalWithAttachments
				show={openCloseWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchData();
					}
					setOpenCloseWorkOrderModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<OpenWorkOrderModal
				show={openWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchData();
					}
					setOpenWorkOrderModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<DeleteWorkOrderModal
				show={openDeleteWorkOrderModal}
				data={selectedRow}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchData();
					}
					setOpenDeleteWorkOrderModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<DeleteMultipleWorkOrdersModal
				show={openDeleteMultipleWorkOrdersModal}
				data={selectedRows}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						searchData();
					}
					setOpenDeleteMultipleWorkOrdersModal(false);
				}}
				suffixTitle="Work Orders"
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal error={craftError} onHide={() => setCraftError("")} />
		</>
	);
}
